import { EyeOutlined, LogoutOutlined, PrinterOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Flex, Modal, Space, Typography } from 'antd';
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { clearPortalSession, hasPortalSession } from './api';
import { usePortalBoard } from './hooks';
import { PaperWorkOrderSheet } from './PaperWorkOrderSheet';
import type { Department, DepartmentTask, WorkOrder } from './types';
import { TaskCard } from './components';
import { EmptyState, PageSkeleton } from './ui';

const { Text, Title } = Typography;

type PortalSelection = {
  task: DepartmentTask;
  order: WorkOrder;
};

export function PortalPage() {
  const navigate = useNavigate();
  const { data, isLoading, isError } = usePortalBoard();
  const [selected, setSelected] = useState<PortalSelection | null>(null);
  if (!hasPortalSession()) return <Navigate to="/login?tab=portal" replace />;
  if (isLoading) {
    return (
      <div className="zen-portal-page">
        <PageSkeleton />
      </div>
    );
  }
  if (isError || !data) return <Navigate to="/login?tab=portal" replace />;
  const byId = new Map(data.workOrders.map((order) => [order.id, order]));
  const pending = data.tasks.filter((task) => task.status === 'pending');
  const started = data.tasks.filter((task) => task.status === 'started');

  const openTask = (task: DepartmentTask) => {
    const order = byId.get(task.workOrderId);
    if (order) setSelected({ task, order });
  };

  return (
    <div className="zen-portal-page">
      <Flex className="zen-portal-header" justify="space-between" align="center" gap={16} wrap>
        <div>
          <div className="ua-overline">Workshop view · {data.departments.map((department) => department.name).join(' · ')}</div>
          <Title level={3} className="ua-portal-title">Pending {pending.length} · Started {started.length}</Title>
        </div>
        <Space size={16}>
          <Text type="secondary"><EyeOutlined /> View only · Refreshes every 30 seconds</Text>
          <Button icon={<LogoutOutlined />} onClick={() => { clearPortalSession(); navigate('/login?tab=portal'); }}>Exit</Button>
        </Space>
      </Flex>

      <Space orientation="vertical" size={16} style={{ width: '100%' }}>
        <PortalColumn title="Pending" count={pending.length}>
          {pending.map((task) => {
            const order = byId.get(task.workOrderId);
            return order && (
              <TaskCard
                key={task.id}
                task={task}
                order={order}
                onOpen={() => openTask(task)}
                showDepartment
                readOnly
              />
            );
          })}
        </PortalColumn>
        <PortalColumn title="Started" count={started.length}>
          {started.map((task) => {
            const order = byId.get(task.workOrderId);
            return order && (
              <TaskCard
                key={task.id}
                task={task}
                order={order}
                onOpen={() => openTask(task)}
                showDepartment
                readOnly
              />
            );
          })}
        </PortalColumn>
      </Space>

      <PortalWorkOrderModal
        selection={selected}
        departments={data.departments}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}

function PortalWorkOrderModal({
  selection,
  departments,
  onClose,
}: {
  selection: PortalSelection | null;
  departments: Department[];
  onClose: () => void;
}) {
  const order = selection?.order ?? null;
  const task = selection?.task ?? null;
  const isCancelled = order?.status === 'cancelled';

  return (
    <Modal
      className="zen-worker-modal zen-responsive-modal"
      open={Boolean(order && task)}
      onCancel={onClose}
      width={860}
      footer={
        <Flex justify="space-between" align="center" gap={12} wrap className="zen-worker-modal-footer">
          <Button icon={<PrinterOutlined />} onClick={() => window.print()}>Print</Button>
          <Button onClick={onClose}>Close</Button>
        </Flex>
      }
    >
      {order && task && (
        <Space orientation="vertical" size={16} style={{ width: '100%', paddingTop: 8 }}>
          <Text type="secondary">View-only · {task.departmentName}</Text>
          {isCancelled && (
            <Alert
              type="error"
              showIcon
              title="This work order was cancelled"
              description="It is permanently read-only."
            />
          )}
          {task.otherItemsIncomplete && ['Delivery', 'Installation', 'Transportation'].includes(task.departmentName) && (
            <Alert
              type="warning"
              showIcon
              title="Other items are still in production"
              description="This item can be handed over separately, but verify that a partial installation or delivery run is intentional."
            />
          )}
          <PaperWorkOrderSheet order={order} task={task} departments={departments} />
        </Space>
      )}
    </Modal>
  );
}

function PortalColumn({ title, count, children }: { title: string; count: number; children: React.ReactNode }) {
  return (
    <Card title={<span>{title} <span className="ua-count">{count}</span></span>}>
      <div className="zen-task-grid">
        {children}
        {count === 0 && <EmptyState compact title={title === 'Pending' ? 'No jobs waiting' : 'Nothing in progress'} />}
      </div>
    </Card>
  );
}
